import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  TruckIcon,
} from '@heroicons/react/24/outline'
import { consignmentService } from '../../services/consignmentService'

interface Consignment {
  consignment_id: number
  consignment_number: string
  issue_date: string
  expected_return_date: string | null
  status: string
  customers: {
    company_name: string | null
    first_name: string | null
    last_name: string | null
  } | null
  consignment_items: {
    item_id: number
    quantity_issued: number
    quantity_sold: number
    quantity_returned: number
    products: {
      product_name: string
    }
  }[]
}

export const ConsignmentsPage = () => {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)

  const { data, isLoading } = useQuery({
    queryKey: ['consignments', search, statusFilter, page],
    queryFn: () => consignmentService.getAll({ search, status: statusFilter, page, limit: 20 }),
  })

  const { data: agingData } = useQuery({
    queryKey: ['consignments', 'aging'],
    queryFn: () => consignmentService.getAging(60),
  })

  const consignments: Consignment[] = data?.data?.data?.items || []
  const pagination = data?.data?.data?.pagination
  const agingCount = agingData?.data?.data?.length || 0

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-blue-100 text-blue-800'
      case 'partially_sold': return 'bg-yellow-100 text-yellow-800'
      case 'fully_sold': return 'bg-green-100 text-green-800'
      case 'recalled': return 'bg-purple-100 text-purple-800'
      case 'closed': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getCustomerName = (consignment: Consignment) => {
    if (!consignment.customers) return 'Unknown'
    if (consignment.customers.company_name) return consignment.customers.company_name
    return `${consignment.customers.first_name || ''} ${consignment.customers.last_name || ''}`.trim()
  }

  const getProgress = (items: Consignment['consignment_items']) => {
    const total = items.reduce((sum, item) => sum + item.quantity_issued, 0)
    const sold = items.reduce((sum, item) => sum + item.quantity_sold, 0)
    return total > 0 ? Math.round((sold / total) * 100) : 0
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Consignments</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage stock issued to consignment partners
          </p>
        </div>
        <div className="flex items-center gap-3">
          {agingCount > 0 && (
            <div className="px-4 py-2 bg-yellow-50 text-yellow-700 rounded-lg">
              <span className="text-sm font-medium">{agingCount} aging consignments</span>
            </div>
          )}
          <Link
            to="/consignments/new"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <PlusIcon className="w-5 h-5" />
            New Consignment
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search consignments..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="partially_sold">Partially Sold</option>
            <option value="fully_sold">Fully Sold</option>
            <option value="recalled">Recalled</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      {/* Consignments List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : consignments.length === 0 ? (
        <div className="bg-white rounded-xl shadow border border-gray-100 p-12 text-center">
          <TruckIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No consignments found</h3>
          <p className="text-gray-500 mb-4">
            {search ? 'Try adjusting your search terms' : 'Get started by creating your first consignment'}
          </p>
          {!search && (
            <Link
              to="/consignments/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <PlusIcon className="w-5 h-5" />
              New Consignment
            </Link>
          )}
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Consignment #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partner</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issue Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {consignments.map((consignment) => {
                    const progress = getProgress(consignment.consignment_items)
                    const totalItems = consignment.consignment_items.reduce((sum, item) => sum + item.quantity_issued, 0)
                    const soldItems = consignment.consignment_items.reduce((sum, item) => sum + item.quantity_sold, 0)
                    
                    return (
                      <tr key={consignment.consignment_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <Link 
                            to={`/consignments/${consignment.consignment_id}`}
                            className="text-sm font-medium text-primary-600 hover:underline"
                          >
                            {consignment.consignment_number}
                          </Link>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-gray-900">{getCustomerName(consignment)}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-600">
                            {new Date(consignment.issue_date).toLocaleDateString()}
                          </p>
                          {consignment.expected_return_date && (
                            <p className="text-xs text-gray-500">
                              Return: {new Date(consignment.expected_return_date).toLocaleDateString()}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {consignment.consignment_items.length} products ({totalItems} units)
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden w-24">
                              <div 
                                className="h-full bg-primary-500 rounded-full"
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-600">{soldItems}/{totalItems}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(consignment.status)}`}>
                            {consignment.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <Link
                            to={`/consignments/${consignment.consignment_id}`}
                            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                          >
                            Manage
                          </Link>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-between bg-white rounded-xl shadow border border-gray-100 p-4">
              <p className="text-sm text-gray-500">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} consignments
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                  disabled={page === pagination.pages}
                  className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
